import { useEffect } from 'react'
import { IconArrowRightDefault } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { track } from '@mc/track-event'
import { TrackingContainer } from '@mc/visibility'
import Image from 'components/LegacyImage'
import { trackViewedCategoryDynamicHeader } from 'libs/dynamic-hero'
import { CategoryHeroMobile } from 'pages/categories/components/CategoryHeroMobile'
import Arrow from '../../assets/red-arrow.svg'
import { trackCatalogPageRowViewed } from '../../events'
import { usePageData } from '../PageDataProvider'
import { HeroDesktop } from './HeroDesktop'
import { HeroTablet } from './HeroTablet'
import { SubcatSelectionTabs } from './SubcatSelectionTabs'
import { useDynamicCategoryHeadlines } from './useDynamicCategoryHeadlines'
import styles from './Hero.module.scss'

export const Hero = () => {
  const { t } = useTranslation('@mc/next')

  const { subcategories, slug, dynamicSubcategoryNames } = usePageData()
  const { headline, subheadline, isDynamic } = useDynamicCategoryHeadlines()

  // Reorder + rename subcategories based on the GrowthBook map.
  // Keys in the map define the new order: mapped subcategories first
  // (in key order), followed by the rest in their original API order.
  const orderedSubcategories = dynamicSubcategoryNames
    ? [
        ...Object.keys(dynamicSubcategoryNames)
          .map((s) => subcategories.find((sub) => sub.slug === s))
          .filter(
            (sub): sub is (typeof subcategories)[number] => sub !== undefined,
          ),
        ...subcategories.filter(
          (sub) => !(sub.slug in dynamicSubcategoryNames),
        ),
      ]
    : subcategories

  useEffect(() => {
    if (isDynamic) {
      trackViewedCategoryDynamicHeader({
        headline,
        subheadline,
        categorySlug: slug,
        isDynamicHero: isDynamic,
      })
    }
    if (dynamicSubcategoryNames) {
      track('Viewed Category Dynamic Subcategories', {
        category_slug: slug,
        subcategory_name_overrides: dynamicSubcategoryNames,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <TrackingContainer
        onFirstVisible={() =>
          trackCatalogPageRowViewed({
            rowName: `hero:${headline}`,
            categorySlug: slug,
          })
        }
      >
        <HeroDesktop />
        <HeroTablet />
        <CategoryHeroMobile />
      </TrackingContainer>

      <TrackingContainer
        onFirstVisible={() =>
          trackCatalogPageRowViewed({
            rowName: 'sub_category_link_section',
            categorySlug: slug,
          })
        }
      >
        <div className='d-flex flex-column'>
          <div
            className={`d-flex align-items-center justify-content-md-start mc-mb-4 mc-mt-5 ${styles.textOnTop}`}
          >
            <h3 className='mc-mr-2' id='subcategories-list'>
              {t('categories.categoryPage.hero.subcategories.title')}
            </h3>
            <Arrow />
          </div>
          <ul className='row no-gutters' aria-labelledby='subcategories-list'>
            {orderedSubcategories.map((subcat, index) => {
              const displayName =
                dynamicSubcategoryNames?.[subcat.slug] || subcat.name

              return (
                <li
                  key={subcat.id}
                  className='col-12 col-md-6 mc-mb-4 col-lg-4'
                >
                  <SubcatSelectionTabs
                    categorySlug={slug}
                    index={index}
                    subcatName={displayName}
                    subcatSlug={subcat.slug}
                  >
                    <div className='d-flex align-items-center'>
                      <Image
                        src={subcat.coursePreview.primary1x1}
                        width={40}
                        height={40}
                        className='mc-corners--circle flex-shrink-0'
                        alt=''
                      />
                      <p
                        className='mc-ml-3 mc-text-h6 flex-1'
                        title={displayName}
                      >
                        {displayName}
                      </p>
                    </div>
                    <div className='flex-shrink-0 d-inline-flex align-items-center justify-content-center mc-corners--circle mc-bg-gray-200 mc-p-2 mc-ml-3'>
                      <IconArrowRightDefault size='sm' />
                    </div>
                  </SubcatSelectionTabs>
                </li>
              )
            })}
          </ul>
        </div>
      </TrackingContainer>
    </>
  )
}
